// Constantes del negocio y del sistema, cada una con su fuente. Congeladas: si una cambia, cambia aquí y en ningún otro lado.

/** Modelo de IA POR OMISIÓN: Claude Sonnet 5.5 desde el 12-oct-2026 (decisión de Diego con la medición, adenda «IA
 *  para leer recibos»: las fotos y los escaneos los lee Sonnet 5.5 —16 de 16 respuestas válidas y 100 % en cifras—; los
 *  PDF digitales, el lector sin IA). El director puede elegir otro de MODELOS_IA en Configuración (contrato v2); uno
 *  elegido a propósito (pantalla o IA_MODELO) se respeta. Nunca uno fuera de esa lista sin que Diego lo diga. */
export const MODELO_IA = 'claude-sonnet-5-5';

/** El modelo por omisión ANTES del 12-oct-2026 (lo que pidió Diego en PLAN-NOCHE §2). Solo para dejar en la bitácora el
 *  cambio de omisión en una base que ya existía (apps/servidor → prepararServidor). */
export const MODELO_IA_ANTERIOR = 'claude-opus-5-5';

/** El modelo con que la cascada vuelve a leer lo que no cuadró cuando el vigente no es un Opus (paquetes/ia →
 *  modeloFuerte). */
export const MODELO_IA_FUERTE = 'claude-opus-5-5';

/** Precio de un modelo en USD por millón de tokens. */
export interface PrecioModeloIA {
  readonly entrada: number;
  readonly salida: number;
  readonly cache_lectura: number;
  readonly cache_escritura: number;
  /** Tarifa para peticiones LARGAS, si el modelo tiene dos (Haiku 5.5: con más de 100K tokens de entrada todo se cobra a
   *  esta). La entrada que cuenta es la de la petición completa (entrada + lectura y escritura de caché). */
  readonly largo?: {
    readonly mas_de_tokens_entrada: number;
    readonly entrada: number;
    readonly salida: number;
    readonly cache_lectura: number;
    readonly cache_escritura: number;
  };
}

/** Precios de la API de Anthropic para MODELO_IA (Sonnet 5.5), en USD por millón de tokens.
 *  Fuente: skill claude-api, tabla "Current Models" (cacheada 2026-10-06). Escritura de caché de 5 min = 1.25 × entrada. */
export const PRECIO_IA_USD_POR_MTOK = Object.freeze({
  entrada: 2,
  salida: 10,
  cache_lectura: 0.2,
  cache_escritura: 2.5,
  fuente: 'skill claude-api, tabla de modelos cacheada el 2026-10-06',
});

/** Modelos que el director puede elegir (selector de Configuración → IA). Agregar uno = agregarlo AQUÍ con su precio y su
 *  fuente (sin precio no hay tope de gasto que valga). Fuente de los precios: skill claude-api, tabla "Current Models"
 *  cacheada el 2026-10-06 y la sección de cada modelo; lectura de caché = 0.1 × entrada, escritura de 5 min = 1.25 ×
 *  entrada. Opus 5 y Opus 4.8 son además los modelos de RESPALDO que Anthropic usa con `fallbacks` (paquetes/ia los cobra
 *  a su tarifa); Opus 4.8 y Haiku 5.5 no tienen a su vez respaldo (medido: paquetes/ia/src/ajustes.ts → RESPALDOS_MEDIDOS). 12-oct: Sonnet 5.5 y Haiku 5.5 para las FOTOS de recibos (lo que el lector sin IA no lee); con la
 *  medición, Diego decidió Sonnet 5.5 como el de omisión (MODELO_IA). */
export const IDS_MODELOS_IA = ['claude-opus-5-5', 'claude-opus-5', 'claude-opus-4-8', 'claude-sonnet-5-5', 'claude-haiku-5-5'] as const;
export type IdModeloIA = (typeof IDS_MODELOS_IA)[number];
export interface ModeloIADisponible {
  readonly nombre: string;
  /** El de omisión (MODELO_IA), el que decidió Diego. Solo uno. */
  readonly recomendado: boolean;
  readonly precio: PrecioModeloIA;
  /** Una línea para el selector de Configuración (para qué sirve y qué cuidar). */
  readonly nota: string;
}
/** Un Record: si se agrega un id a IDS_MODELOS_IA sin su precio, NO compila. */
export const MODELOS_IA: Readonly<Record<IdModeloIA, ModeloIADisponible>> = Object.freeze({
  'claude-opus-5-5': {
    nombre: 'Claude Opus 5.5',
    recomendado: false,
    precio: { entrada: 4, salida: 20, cache_lectura: 0.2, cache_escritura: 5 },
    nota: 'El Opus vigente: el doble de precio que Sonnet 5.5. Si se niega, Anthropic reintenta con su modelo de respaldo.',
  },
  'claude-opus-5': {
    nombre: 'Claude Opus 5',
    recomendado: false,
    precio: { entrada: 5, salida: 25, cache_lectura: 0.5, cache_escritura: 6.25 },
    nota: 'El Opus anterior (más caro que Opus 5.5).',
  },
  'claude-opus-4-8': {
    nombre: 'Claude Opus 4.8',
    recomendado: false,
    precio: { entrada: 5, salida: 25, cache_lectura: 0.5, cache_escritura: 6.25 },
    nota: 'Opus de generación anterior. Sin modelo de respaldo si se niega.',
  },
  'claude-sonnet-5-5': {
    nombre: 'Claude Sonnet 5.5',
    recomendado: true,
    precio: { entrada: 2, salida: 10, cache_lectura: 0.2, cache_escritura: 2.5 },
    nota: 'El de omisión (decisión de Diego, 12-oct): leyó bien 16 de 16 fotos y escaneos de la medición, a la mitad de precio que Opus 5.5. Si se niega, Anthropic reintenta con su modelo de respaldo.',
  },
  'claude-haiku-5-5': {
    nombre: 'Claude Haiku 5.5',
    recomendado: false,
    precio: {
      entrada: 0.1,
      salida: 0.5,
      cache_lectura: 0.01,
      cache_escritura: 0.125,
      largo: { mas_de_tokens_entrada: 100_000, entrada: 0.5, salida: 2.5, cache_lectura: 0.05, cache_escritura: 0.625 },
    },
    nota: 'El más barato (40 veces menos que Opus 5.5; con más de 100K tokens de entrada, 8 veces menos). Sin modelo de respaldo si se niega.',
  },
});

/** Cascada para leer recibos (12-oct, APAGADA por omisión): lo que el lector sin IA no lee (fotos, escaneos) lo lee primero
 *  un modelo barato; si las cuentas de ese recibo no cuadran o el modelo marca duda, ese recibo se vuelve a leer con Opus. */
export const MODELOS_CASCADA = ['claude-haiku-5-5', 'claude-sonnet-5-5'] as const satisfies readonly IdModeloIA[];
export const CASCADA_IA_POR_OMISION = Object.freeze({ activa: false, modelo_barato: 'claude-haiku-5-5' as (typeof MODELOS_CASCADA)[number] });

/** ADENDA A0 (10-oct-2026): FINSUS de REFERENCIA. Enganche y plazo con los que se calcula SIEMPRE la mensualidad
 *  (la fórmula y la tasa viven en paquetes/motor/datos/finsus.json, copia fiel de financieras.json de la plantilla). */
export const FINSUS_REFERENCIA = Object.freeze({
  enganche_pct: 10,
  plazo_meses: 60,
  /** Leyenda chiquita (≥ 7.5 pt en el PDF; letra de notas en el cotizador). {tasa} y {cat} se llenan con lo calculado
   *  (textoLeyendaFinsus en dominio/opciones.ts). Nunca "crédito bancario" ni "cotización FINSUS". */
  leyenda: 'Cálculo de mensualidad de referencia con FINSUS; tasa {tasa} % anual, CAT promedio {cat} % sin IVA, informativo. Sujeto a aprobación de crédito.',
  /** Producto «empresarial» (persona moral o PF con actividad empresarial, 16-oct-2026): sin CAT (FINSUS no dio CAT de ese producto y el CAT nunca se
   *  calcula) y con la comisión por apertura de contado. */
  leyenda_empresarial: 'Cálculo de mensualidad de referencia con FINSUS, crédito empresarial; tasa {tasa} % anual sin IVA en intereses, comisión por apertura de contado. Sujeto a aprobación de crédito.',
  /** Diego, 28-oct-2026 (manda sobre A0 y z21): en los dos productos, tasa 15.99 % a 60 meses y el seguro de daños y la
   *  comisión por apertura DE CONTADO; sin CAT (las referencias son de las condiciones viejas). Las usa textoLeyendaFinsus
   *  cuando el FINSUS trae seguro_danos_contado; las dos de arriba quedan para los resultados de antes. */
  leyenda_contado: 'Cálculo de mensualidad de referencia con FINSUS, informativo; tasa {tasa} % anual más IVA a 60 meses; seguro de daños y comisión por apertura de contado. Sujeto a aprobación de crédito.',
  leyenda_empresarial_contado: 'Cálculo de mensualidad de referencia con FINSUS, crédito empresarial, informativo; tasa {tasa} % anual sin IVA en intereses a 60 meses; seguro de daños y comisión por apertura de contado. Sujeto a aprobación de crédito.',
  fuente: 'ADENDA-DIEGO.md §A0, 10-oct-2026; condiciones de contado: Diego, 28-oct-2026',
});

/** Umbrales de COSTOS FIJOS (UVIE + UI, trámites…) como % del precio de lista sin IVA de la opción, para cuando la tabla
 *  de precios todavía no trae los suyos (TablaPrecios.umbrales.costos_fijos_*). Propuesta del agente IA del 9-oct-2026,
 *  SIN CONFIRMAR por Diego. Amarillo desde `alerta_pct`; rojo desde `rojo_pct`. Léelos con umbralesCostosFijos(). */
export const UMBRALES_COSTOS_FIJOS_POR_OMISION = Object.freeze({
  alerta_pct: 20,
  rojo_pct: 35,
  fuente: 'Propuesta del agente IA (9-oct-2026), sin confirmar por Diego',
});

/** Tres opciones por cotización (memoria formatos-cotizacion; ESPEC 2.7). Cobertura = generación anual ÷ consumo anual. */
export const COBERTURA_OBJETIVO = Object.freeze({ arranque: 0.6, exacta: 1.0, holgada: 1.15 });
/** Banda HABITUAL de cada opción: con la cobertura por omisión los paneles no se salen de ella. Diego, 12-oct: fuera de la
 *  banda (cobertura escrita por el vendedor o paneles a mano) ya NO truena el PDF: es aviso y la propuesta dice
 *  "Cubre X % de lo que usas". */
export const BANDA_COBERTURA = Object.freeze({
  arranque: { min: 0.5, max: 0.75 },
  exacta: { min: 0.95, max: 1.1 },
  holgada: { min: 1.08, max: 1.3 },
});
/** Lo único que sí truena (motor y plantilla): una opción que cubre menos de 20 % o más de 200 % del consumo. */
export const COBERTURA_LIMITE = Object.freeze({ min: 0.2, max: 2 });
/** Barrido del 25-oct (R3, 3a; D4 de Diego: rojo honesto y se queda el 60 %): con aumento de consumo, la Holgada debe cubrir al
 *  menos esto del consumo FUTURO (lo que cubre un Arranque, ESPEC 2.7). Espejo de COBERTURA_FUTURA_MIN (en %) de la plantilla,
 *  variantes/aumento_consumo.mjs (prueba espejo en paquetes/motor/src/ronda-avisos.test.ts): debajo, la plantilla truena. */
export const COBERTURA_FUTURA_MIN = 0.6;

/** Baterías POR OPCIÓN (Diego, 24-oct-2026, decisiones 1-3): cada opción de paneles lleva SU nivel de baterías, uno a uno.
 *  Es fijo (así se lee `parametros.modulos_respaldo`: esencial = Arranque…); el esquema de NivelRespaldoCalculado lo exige
 *  cuando el nivel dice su `opcion`. */
export const NIVEL_DE_OPCION = Object.freeze({ arranque: 'esencial', exacta: 'confort', holgada: 'completa' } as const);
/** Por omisión, escalonado (decisión 2): Arranque ~2 h, Exacta ~4 h, Holgada ~8 h de toda la casa («Hasta N horas»). El
 *  vendedor mueve las baterías de cada opción; esto es solo el punto de partida. */
export const HORAS_OBJETIVO_DE_OPCION = Object.freeze({ arranque: 2, exacta: 4, holgada: 8 } as const);
/** ADENDA z48-1 (Diego, 28-oct-2026; MANDA sobre las ~2/4/8 h de arriba y la z28 en el HÍBRIDO): «solo lo quieren para apagones
 *  comunes». Cada opción del híbrido NUEVO arranca con las baterías que dejan sus horas lo más cerca de 1 a 2 h (nunca 0 y al
 *  menos 1 h con la carga máxima, como pide la plantilla), un nivel por opción. El aislado sigue por noches. Lo prende
 *  ParametrosCalculo.respaldo_1a2_horas; las horas que escribe el vendedor por opción van en ParametrosCalculo.horas_respaldo. */
export const HORAS_RESPALDO_HIBRIDO = Object.freeze({ min: 1, max: 2 } as const);
/** Diego, 2-nov-2026 (MANDA): «siempre que sean aislados o híbridos contempla trabajos extras: cuesta un poco más la parte eléctrica,
 *  normalmente se duplica. […] Toma esta regla para todos los aislados e híbridos siempre en el cotizador». Primero pidió 2.5 veces y el
 *  mismo día lo bajó: «mejor que sea 2x en vez de 2.5 y publícala». COSTO: el material eléctrico de la opción (tabla.costos_por_tamano.material_electrico_usd_por_w)
 *  va a este factor. PRECIO: lo de MÁS (factor − 1) se cobra como la diferencia del híbrido (ADENDA z4): ÷ (1 − margen de alerta − comisión
 *  de los internos) ÷ (1 − descuento de contado). Va en la parte de baterías del nivel (respaldo.ts), no en el precio de los paneles: los
 *  paneles siguen costando lo mismo que solos y su retorno no cambia (Diego, 14-oct 22:30). Sin renglón en el PDF.
 *  Parámetro de versión `electrica_hibrido_aislado`: solo las NUEVAS (dominio/version.ts); las guardadas no cambian solas.
 *  NO entran: el respaldo sin paneles y el aislado sin paneles (no hay W de paneles sobre los cuales medir la parte eléctrica; sus fijos de
 *  instalación siguen igual), la ampliación de baterías (no tiene parte eléctrica de sistema) y el recorte de punta. En un híbrido sobre una
 *  ampliación se mide con los paneles NUEVOS. */
export const FACTOR_ELECTRICA_HIBRIDO_AISLADO = 2;
/** ADENDA z53-4 (Diego, 28-oct-2026; MANDA): los inversores DE RED van con Growatt POR OMISIÓN y el que cotiza puede cambiarlos
 *  por otra marca de las listas en Opciones (ParametrosCalculo.marca_inversor_red); el precio sube solo por la diferencia de costo
 *  (regla de extras de z24). Solo marcas aptas para México (UL 1741 + IEEE 1547, sin recortar el TCL 620 W) y con su garantía en la
 *  plantilla del PDF. La primera es la de omisión. El motor truena al arrancar si datos/equipos.json no dice lo mismo. */
/** Hoy SOLO Growatt (verificación del 28-oct noche): Solis entra con la D1 de PROPUESTA-OCT-2026 (¿su garantía International para
 *  Latinoamérica cuenta como «garantía de Solis en México por escrito», z53-3?), en su propio commit. Con una sola marca la pantalla no
 *  enseña el selector y nadie guarda marca_inversor_red: regresar a una versión anterior no rompe ninguna cotización. Agregar una
 *  marca después solo AMPLÍA la lista (las guardadas siguen valiendo); quitarla, no se puede (las que la pidieron ya no se guardarían). */
export const MARCAS_INVERSOR_RED = Object.freeze(['Growatt'] as const);
export type MarcaInversorRed = (typeof MARCAS_INVERSOR_RED)[number];
/** INTERRUPTOR DE LA D2 + D4 (29-oct-2026, verificación de hoy69; ADENDA z55(4): «lo conservador, no prometer de más»). APAGADA.
 *  La versión de octubre de baterías (parámetro de versión `respaldo_oct26`: Growatt ya no va de principal, el híbrido principal es el
 *  LuxPower GEN-LB-US 13K/16K y la batería por omisión la Pylontech UF5000, a costo de DMSolar desde la ADENDA z57: 845 USD + cables
 *  y soporte = 909 USD por batería; z57 pide también la carta de DMSolar como distribuidor AUTORIZADO de Pylontech) NO nace en ninguna cotización:
 *  el par UF5000 + GEN-LB-US 13K/16K no está en la lista de compatibilidad de Pylontech (Ver.2.46) y la garantía de la UF5000 excluye
 *  inversores que Pylontech no certifica, pero el PDF imprimiría «Pylontech · UF5000 · 10 años · Responde: Fabricante». Con false:
 *  baterías, híbridos y aislados nuevos eligen EXACTAMENTE como producción 7b9adaa (version.ts → respaldoOct26Aplica), el servidor no
 *  deja prender `respaldo_oct26` (recalculo.ts) y nube/agregar-renglon-tabla.mjs no escribe el renglón `pylontech-uf5000` mientras el
 *  commit de producción no traiga esta línea en true (candado 7). El trabajo NO se borró: el motor de la D2 y sus pruebas siguen.
 *  PRENDERLA (cuando llegue la carta de Pylontech o de LuxPower que diga que la UF5000 con el GEN-LB-US 13K/16K conserva su garantía,
 *  y Diego decida la D4): cambiar la línea a `export const D4_ACTIVA: boolean = true;` (EXACTA: el candado 7 la busca así), escribir
 *  en D4_CARTA de dónde sale la carta (archivo, fecha, quién firma) y seguir ESTADO-HOY69.md «Cómo prender la D4». Sin la carta, truena
 *  al cargar (abajo). La prueba paquetes/motor/src/d4-apagada.test.ts vigila las dos posiciones. */
export const D4_ACTIVA: boolean = false;
/** La carta de Pylontech o LuxPower que permite prender la D4 (archivo, fecha y quién firma; al menos 40 caracteres). null = no ha llegado. */
export const D4_CARTA = null as string | null;
if (D4_ACTIVA && (D4_CARTA === null || D4_CARTA.trim().length < 40))
  throw new Error('compartido: D4_ACTIVA es true sin D4_CARTA: la D2 + D4 (UF5000 + LuxPower GEN-LB-US por omisión) solo se prende con la carta de Pylontech o LuxPower escrita en D4_CARTA (ESTADO-HOY69.md, «Cómo prender la D4»)');
// Los parámetros de versión (los que nacen prendidos en las cotizaciones nuevas) viven en UNA lista: dominio/version.ts.
/** hoy99 (Diego, 2-nov-2026: «Sí, aplícalas», tarifas de noviembre de CFE): hasta qué mes de 2026 cuenta la tabla de CFE una cotización
 *  NUEVA (parámetro de versión `cuotas_cfe_hasta`). Una guardada no lo trae y sigue con CUOTAS_CFE_SIN_PARAMETRO: el ahorro de una
 *  propuesta ya enviada no se mueve solo. Al aplicar otro mes con scripts/tarifas-cfe.mjs: súbelo y agrega su renglón a
 *  CONSULTAS_CFE (motor/src/cfe.ts); paquetes/motor/src/cuotas-cfe-hasta.test.ts truena si no. */
export const CUOTAS_CFE_HASTA_MES = 11;
export const CUOTAS_CFE_SIN_PARAMETRO = 10;
/** AISLADO por opción (Diego, 24-oct-2026, decisión 7; E7): las opciones se distinguen por NOCHES de autonomía. Arranque = la
 *  noche justa · Exacta = la noche + 50 % · Holgada = 2 noches (el día nublado). El banco = energía de noche de sus equipos ×
 *  noches × (1 + reserva); sus paneles dan el día y recargan ese banco. */
export const NOCHES_DE_OPCION = Object.freeze({ arranque: 1, exacta: 1.5, holgada: 2 } as const);

/** Descuento de contado sobre precio de lista (Diego, 8-oct; el generador lo comprueba ± $2). */
export const DESCUENTO_CONTADO_PCT = 15;

/** ADENDA A0b (10-oct): cualquier vendedor BAJA el precio hasta 5 % contra la tabla sin aprobación (además del 15 % de
 *  contado); más pide el OK de Diego. Subir es libre. Diego lo edita en la tabla (umbrales.descuento_max_sin_aprobacion_pct). */
export const DESCUENTO_MAX_SIN_APROBACION_POR_OMISION = 5;

/** Diego, 13-oct (MANDA): Diego, Miguel Cota y Juan Carlos Cortés (los que pueden autorizar un descuento: director y
 *  asesores internos, nunca un vendedor de Optimus) bajan hasta 10 % contra la tabla SIN OK, aunque el semáforo deje de
 *  estar verde ("a veces quito lo de la comisión o es un cliente para no perderlo si sacrificamos margen"). El PISO sigue
 *  siendo solo de Diego; los externos siguen con DESCUENTO_MAX_SIN_APROBACION_POR_OMISION y "verde manda". Diego lo edita
 *  en la tabla (umbrales.descuento_max_sin_aprobacion_internos_pct). */
export const DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION = 10;

/** VERDE DE ENTRADA (Diego, 2-nov-2026: «siempre que el cotizador de entrada dé en verde en todos los proyectos, ya después el vendedor va a
 *  ver si le baja a amarillo»). Las decisiones provisionales de la regla viven aquí: `comision_minima_pct`: el mínimo se calcula con la MAYOR
 *  entre esta y la comisión real (hasta 6 % es el mismo precio; con 10 o 15 % sube); `aviso_subida_pct`: arriba de esto sobre la tabla, aviso
 *  amarillo (sin tope: sistemas diminutos). Quien las cambie: motor/src/verde-de-entrada.ts las usa, y su prueba truena si cambia la regla. */
export const VERDE_DE_ENTRADA = Object.freeze({ comision_minima_pct: 6, aviso_subida_pct: 10 } as const);

/** ADENDA A0b + COSTOS-DIEGO (10-oct): comisión sobre el contado sin IVA que se resta en la rentabilidad. Externos 5 %,
 *  internos (Miguel Cota, Juan Carlos Cortés) 6 %, Diego cotiza SIN comisión. Editable por vendedor (Usuario.comision_pct;
 *  Iván Arredondo 10–12 %) y por cotización (ParametrosCalculo.comision_pct). */
export const COMISION_POR_ROL = Object.freeze({ director: 0, asesor: 6, externo: 5, tecnico: 0 } as const);

/** OPTIMUS, aliado comercial de Solarix (Diego, 12-oct noche, ADENDA punto c, MANDA): sus vendedores (Zorayda y Alfredo
 *  Mendoza; NO Iván) cobran su comisión (10 % por omisión) y Juan Carlos Cortés 5 % más: 15 %. El PRECIO sube para que
 *  Solarix gane lo mismo que en una venta normal con 6 %: tabla × (1 − 6 %) ÷ (1 − 15 %) ≈ +10.6 %. Leyenda chica en la
 *  tarjeta del asesor del PDF. Los demás externos (Iván 10 %, Omar Gutiérrez, ocasionales 6–10 %) NO suben de precio. */
export const OPTIMUS = Object.freeze({
  /** Comisión del vendedor de Optimus si Diego no le pone otra en su ficha. */
  comision_vendedor_por_omision_pct: 10,
  /** Lo que cobra Juan Carlos Cortés por cada venta de Optimus (se suma a la del vendedor). */
  comision_aliado_pct: 5,
  aliado_cobra: 'Juan Carlos Cortés',
  /** La comisión de una venta NORMAL con la que Solarix debe ganar lo mismo (la de los internos). */
  comision_base_pct: 6,
  /** Lo que imprime la tarjeta del asesor (texto de Diego). */
  leyenda: 'Asesor de Optimus, aliado comercial de Solarix Energy',
} as const);

/** ADENDA A0b (decisión del 11-oct): el total CON IVA de contado se redondea a los $100 y lo hace el COTIZADOR (fuente
 *  única); la lista es contado ÷ 0.85 (sin redondear). La plantilla valida que subtotal + IVA cuadre con el total. */
export const REDONDEO_CONTADO_PESOS = 100;

/** PRECIO ÚNICO (Diego, 13-oct-2026, 12:50, ADENDA punto 5, MANDA): en comercio e industria, una opción cuyo contado CON IVA
 *  de la tabla pase de este monto lleva UN SOLO PRECIO, el de contado ("arriba del millón de pesos ya nadie paga con tarjeta"):
 *  sin lista, sin 15 % de descuento, sin MSI. El cliente paga lo mismo que de contado. Diego lo edita en la tabla
 *  (umbrales.precio_unico_arriba_de_con_iva); léelo con precioUnicoArribaDe(). */
export const PRECIO_UNICO_ARRIBA_DE_POR_OMISION = 1_000_000;

/** ADENDA A0b: MSI (3, 6, 12) solo en opciones de hasta $500,000 de LISTA con IVA (politicas.json de la plantilla). */
export const MSI_TOPE_LISTA_CON_IVA = 500_000;
/** COSTOS-DIEGO (10-oct): el banco cobró 17.96 % en 3 ventas a meses sin intereses (plazo por confirmar con Diego). La
 *  lista (contado ÷ 0.85) solo cubre 15 %. Diego, 14-oct (z6): 12 MSI van siempre (ya no se avisa ese riesgo). */
export const MSI_COSTO_BANCO_PCT = 17.96;

/** ADENDA A0b: tipo de cambio del cotizador = FIX de Banxico del día + 3 % de colchón (editable). Se CONGELA por folio. */
export const COLCHON_TIPO_CAMBIO_PCT = 3;
/** Con un FIX de la tabla más viejo que esto (días), el motor avisa para que Diego lo actualice. */
export const DIAS_FIX_VIEJO = 7;

/** Diego, 13-oct (pedido 6): si el cliente paga en EFECTIVO se le da HASTA 5 % de descuento sobre el total de contado CON
 *  IVA; el IVA no se quita (lo que se descuenta sale completo de lo que le queda a Solarix). SOLO en pantalla para el
 *  vendedor: el PDF no lo anuncia. */
export const DESCUENTO_EFECTIVO_MAX_PCT = 5;

/** Total en efectivo con el descuento máximo: contado con IVA × 0.95, redondeado a $100 HACIA ARRIBA (así el descuento
 *  nunca pasa de 5 %). La usan el motor (margen y utilidad en efectivo, solo Diego) y la pantalla (la cifra para todos),
 *  así nunca dicen dos cosas distintas. */
export function contadoEnEfectivo(contadoConIva: number): number {
  if (!(contadoConIva > 0)) return 0;
  return Math.ceil((contadoConIva * (1 - DESCUENTO_EFECTIVO_MAX_PCT / 100)) / REDONDEO_CONTADO_PESOS - 1e-9) * REDONDEO_CONTADO_PESOS;
}

/** ADENDA A0b: generación conservadora, 1,750 kWh por kWp al año en TODA Sonora, para dimensionar y para todo lo que se
 *  promete (la plantilla truena arriba de 1,750 + 0.5 % y avisa abajo de 1,750 − 0.5 %). */
export const GENERACION_KWH_KWP_ANIO = 1750;

/** ADENDA A0b: "si una cotización enviada lleva 3 días sin cambio de estado, el cotizador la marca y avisa". */
export const DIAS_SIN_RESPUESTA = 3;

/** Vigencia de la cotización en días (adenda: 30 días). */
export const VIGENCIA_DIAS = 30;

/** Supuestos de la cifra a 25 años (adenda A2): los aplica el generador, aquí solo para mostrarlos igual. Aumento de la luz:
 *  6 % al año desde el 12-oct-2026 (ADENDA, decisiones de Diego del 12-oct noche, punto b: "como SunWise"; antes 5 %). La
 *  plantilla lee el suyo de lib/nucleo/supuestos.json y las dos cifras deben ser la misma (prueba plantilla.test.ts). */
export const SUPUESTOS_25_ANIOS = Object.freeze({ aumento_tarifa_pct: 6, perdida_paneles_pct: 0.42, anios: 25 });

/** Retorno de CONTADO con la luz subiendo (Diego, 12-oct, MANDA: "el retorno incluye el incremento anual de la luz", 6 % desde
 *  el 12-oct noche; el mismo supuesto de la cifra a 25 años): años para recuperar `contado` sumando el ahorro de cada año, año t (0, 1, 2…) =
 *  ahorro · (1 + aumento)^t · (1 − pérdida de los paneles)^t; dentro del año en que se completa, la parte proporcional. Sin
 *  redondear (quien lo muestra redondea a un decimal). La plantilla hace la MISMA cuenta (lib/nucleo/opciones.mjs →
 *  aniosRetorno) y su retorno impreso es el que se compara. Infinity si en 200 años no se paga. */
export function aniosRetorno(contado: number, ahorroAnual: number, s: { aumento_tarifa_pct: number; perdida_paneles_pct: number } = SUPUESTOS_25_ANIOS): number {
  if (!(contado > 0) || !(ahorroAnual > 0)) return Infinity;
  const f = (1 + s.aumento_tarifa_pct / 100) * (1 - s.perdida_paneles_pct / 100);
  let acumulado = 0;
  for (let t = 0; t < 200; t++) {
    const a = ahorroAnual * f ** t;
    if (acumulado + a >= contado) return t + (contado - acumulado) / a;
    acumulado += a;
  }
  return Infinity;
}

/** El supuesto del retorno, escrito junto a él en el cotizador (la plantilla imprime el suyo con las mismas cifras). */
export const TEXTO_SUPUESTO_RETORNO = `De contado, con la luz subiendo ${SUPUESTOS_25_ANIOS.aumento_tarifa_pct} % al año y los paneles perdiendo ${SUPUESTOS_25_ANIOS.perdida_paneles_pct} % de potencia al año.`;

/** Diego, 12-oct (ADENDA g): la leyenda corta del incentivo fiscal, la MISMA que imprime la plantilla junto al segundo retorno
 *  (T.leyendaFiscal en Propuesta v2/plantilla/lib/nucleo/textos-comunes.mjs). */
export const TEXTO_LEYENDA_FISCAL = 'Estimado; valídalo con tu contador: depende de tu situación fiscal y el equipo debe operar al menos 5 años.';

/** RECORTE DE PUNTA (peak shaving) con baterías grandes en GDMTH (ADENDA z54 y z55-2 de Diego, 28-oct-2026; investigación
 *  verificada PEAK-SHAVING-GDMTH-2026-10-28 §5.4). UNA sola copia de las reglas: el motor las aplica, el contrato del resultado
 *  las revisa y la plantilla recibe las mismas en el JSON (estudio.tope_recorte_pct_punta y estudio.meses_fallados).
 *   - tope_pct_punta: el recorte de un mes nunca pasa de 0.9 × su demanda en punta (candado 5);
 *   - meses_fallados: el ahorro y el retorno van en rango también con 2 y 6 meses en que se escapa la punta (candado 4);
 *   - meses_minimos: el recorte tiene que pasar la zona muerta en al menos 8 de 12 meses (candado 3);
 *   - retorno_verde / retorno_tope: verde ≤ 4.0 años (y ≤ 5.0 con 2 meses fallados); hasta 5.0 en amarillo con aviso; arriba de
 *     5.0 no se ofrece (z54-2);
 *   - gabinetes_max: tope de gabinetes en paralelo (la hoja de datos de Livoltek: hasta 10). */
export const RECORTE_PUNTA_REGLAS = Object.freeze({
  tope_pct_punta: 0.9,
  meses_fallados: Object.freeze([2, 6] as const),
  meses_minimos: 8,
  retorno_verde: 4.0,
  retorno_tope: 5.0,
  gabinetes_max: 10,
  /** Desde esta demanda (kW) el cliente puede registrarse como Usuario Calificado y salir del suministro básico (RLSE, Transitorio
   *  Vigésimo Sexto): amarillo D4. */
  usuario_calificado_kw: 1000,
  /** Nicho «trabaja de noche» (z54-1) visto en los recibos: si la mediana del año de demanda en punta ÷ demanda máxima queda abajo de
   *  esto, amarillo «confirma que trabaja de noche» (verificación de hoy70: un turno de día de 1 MW salía verde si el vendedor
   *  contestaba «sí» por error). PROVISIONAL (z55-4, lo conservador): el umbral lo decide Diego. */
  punta_sobre_maxima_min: 0.5,
});

/** Tarifas en que se SUGIEREN los 12 recibos. Diego en la junta (13-oct): «si es GDMTO solo se necesita un recibo»; en GDMTH
 *  se usan los que haya («lo más que puedas… pero que no sea condicionante»): los meses que faltan se estiman con aviso
 *  amarillo. Nunca es obligatorio. */
export const TARIFAS_QUE_PIDEN_12_RECIBOS = Object.freeze(['GDMTH'] as const);

/** Archivos que se aceptan como recibo. HEIC/HEIF se aceptan al subir pero la IA no los lee: el servidor los convierte
 *  o avisa (la API de Anthropic solo lee JPEG, PNG, GIF, WebP y PDF). */
export const TIPOS_ARCHIVO_ACEPTADOS = Object.freeze([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const);
export type TipoArchivo = (typeof TIPOS_ARCHIVO_ACEPTADOS)[number];

/** Tamaño máximo por archivo. La API acepta 32 MB por petición y el base64 crece ~33 %: 20 MB deja margen. */
export const BYTES_MAXIMOS_POR_ARCHIVO = 20 * 1024 * 1024;

/** Fotos para la IA (nube, tanda 1): lo más grande que Opus 5.x aprovecha es 2576 px en el lado largo y ~3.75 MP
 *  (skill claude-api). La IA reduce a esto en el servidor y el NAVEGADOR reduce a lo mismo ANTES de subir (ahorra datos
 *  del celular y espacio, y en Linux no hay `sips`). Una sola fuente para los dos lados. */
export const FOTO_LADO_MAXIMO_PX = 2576;
export const FOTO_MEGAPIXELES_MAXIMOS = 3.75;
/** Peso máximo de una imagen para la API (acepta 5 MB por imagen y el base64 crece ~33 %). */
export const FOTO_BYTES_MAXIMOS = 3_750_000;
/** Calidad JPEG al reducir (0–1): la misma 85 que usaba `sips` en el servidor. */
export const FOTO_CALIDAD_JPEG = 0.85;
/** Máximo de recibos por medidor en una cotización (12 mensuales + margen para recibos repetidos o de más). */
export const RECIBOS_MAXIMOS_POR_MEDIDOR = 18;

/** Nube, tanda 2 (HOSTING §3.3, punto 3): el detalle de una cotización trae solo los ÚLTIMOS eventos del historial; lo de
 *  antes se pide con «Ver anteriores» (GET /api/cotizaciones/:id/eventos). Así recargar el detalle después de cada acción
 *  no crece con el historial (en Datastore cada evento leído es una lectura que se cobra). */
export const EVENTOS_POR_PAGINA = 30;
/** Lo más que se pide de una vez a la ruta del historial. */
export const EVENTOS_POR_PAGINA_MAX = 200;

/** Sesión de acceso: el enlace dura 15 minutos y sirve una vez; la sesión dura 30 días.
 *  (Con Firebase en plan gratis solo salen 5 enlaces al día: sesiones largas para no gastarlos.) */
export const ENLACE_MINUTOS = 15;
export const SESION_DIAS = 30;
export const NOMBRE_COOKIE_SESION = 'cotizador_sesion';

/** Prefijo de folio: SX-AAAA-NNNN (cabe en el patrón del esquema v1 del generador). */
export const PREFIJO_FOLIO = 'SX';

/** Datos del proyecto de Firebase de Solarix (medidos el 9-oct-2026; ver ARQUITECTURA.md §2). No son secretos. */
export const FIREBASE_PROYECTO = 'solarixenergy-924b4';
